/* eslint-disable no-bitwise */
// eslint-disable-next-line import/no-cycle
import { message } from 'antd';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import CryptoJS from 'crypto-js';
import moment from 'moment';

import * as Sentry from '@sentry/react';

const readLeadingTNumber = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const match = value.match(/^T(\d+)/);
  return match ? match[1] : null;
};

const readAgendaClassNumber = (task) =>
  readLeadingTNumber(task.classId) ?? readLeadingTNumber(task.cardTitle);

const readAgendaGroupName = (task) => {
  const groupName = task.group?.name;

  if (typeof groupName !== 'string' || groupName.trim() === '') {
    return null;
  }

  return groupName;
};

const hasAgendaCardTitlePieces = (task) =>
  task.qtyClass > 0 && task.trainingDay > 0 && task.totalTrainingDays > 0;

const AGENDA_GRID_TITLE_NEW_MASK = /^(T\d+\/\d+ - D\d+\/\d+)(?:-.*)?$/;
const AGENDA_GRID_TITLE_OLD_MASK = /^(T\d+-D\d+)(?:-.*)?$/;

const readAgendaGridTitleFromNewMask = (cardTitle) => {
  if (typeof cardTitle !== 'string') {
    return null;
  }

  const match = cardTitle.match(AGENDA_GRID_TITLE_NEW_MASK);
  return match ? match[1] : null;
};

const readAgendaGridTitleFromOldMask = (cardTitle) => {
  if (typeof cardTitle !== 'string') {
    return null;
  }

  const match = cardTitle.match(AGENDA_GRID_TITLE_OLD_MASK);
  return match ? match[1] : null;
};

const readAgendaGridFallbackTitle = (cardTitle) =>
  readAgendaGridTitleFromNewMask(cardTitle) ?? readAgendaGridTitleFromOldMask(cardTitle) ?? '';

const readTrimmedAgendaClientField = (value) => (typeof value === 'string' ? value.trim() : '');

const readAgendaCardClientName = (task) => {
  if (!task) {
    return '';
  }

  // A máscara do card mostra só o Nome do Cliente (`dealClient`).
  // O Nome Fantasia (`dealClientCommercialName`) fica de fora desta linha,
  // mesmo quando está preenchido: não há troca nem fallback para a fantasia.
  return readTrimmedAgendaClientField(task.dealClient);
};

const AGENDA_TRANSPORT_COLOR_YELLOW = '#fabc2c';
const AGENDA_TRANSPORT_COLOR_GRAY = '#b1b1b1';
const OFFICIAL_TRANSPORT_INCLUDED = 'Incluso';

const isOfficialTransportIncluded = (appointmentData) =>
  appointmentData?.trainingTransportation === OFFICIAL_TRANSPORT_INCLUDED;

const hasSelectedTransportCompany = (appointmentData) => {
  const companyId = appointmentData?.transportCompany?.id;
  const numericId = Number(companyId);

  return !Number.isNaN(numericId) && numericId > 0;
};

const readLinkedAgendaVehicles = (appointmentData) =>
  (appointmentData?.driverList || []).map((driver) => driver.vehicle).filter(Boolean);

const formatAgendaVehicleIdentification = (vehicle) => {
  if (typeof vehicle.label === 'string' && vehicle.label.trim() !== '') {
    return vehicle.label.trim();
  }

  const licensePlate = typeof vehicle.licensePlate === 'string' ? vehicle.licensePlate.trim() : '';
  const vehicleTypeName = typeof vehicle.type?.name === 'string' ? vehicle.type.name.trim() : '';

  return [licensePlate, vehicleTypeName].filter(Boolean).join(' - ');
};

const buildHiddenAgendaTransportIcon = () => ({
  visible: false,
  color: null,
  tooltipKind: 'none',
  vehicleIdentifications: [],
});

const buildIncludedAgendaTransportIcon = (linkedVehicles, transportCompanySelected) => {
  const vehicleIdentifications = linkedVehicles
    .map(formatAgendaVehicleIdentification)
    .filter(Boolean);
  const transportIsSelected = linkedVehicles.length > 0 || transportCompanySelected;

  if (!transportIsSelected) {
    return {
      visible: true,
      color: AGENDA_TRANSPORT_COLOR_GRAY,
      tooltipKind: 'incluso',
      vehicleIdentifications: [],
    };
  }

  return {
    visible: true,
    color: AGENDA_TRANSPORT_COLOR_YELLOW,
    tooltipKind: vehicleIdentifications.length > 0 ? 'vehicles' : 'incluso',
    vehicleIdentifications,
  };
};

/**
 * Utilitários compartilhados do frontend do Portal Conecta.
 *
 * Classe estática (não instanciar). Cobre sessão, validação, cores, datas,
 * descriptografia de settings, log de erros e formatação de valores.
 *
 * @example
 * import Utils from '../../Assets/Scripts/Utils';
 * Utils.logError(error);
 */
export default class Utils {
  /**
   * Agrupa itens de um array pela chave informada.
   *
   * @param {Array<object>} array Lista de objetos.
   * @param {string} key Nome da propriedade usada como agrupador.
   * @returns {Object<string, Array<object>>} Mapa chave → lista de itens.
   */
  /**
   * Caminho relativo do Bitrix (`/bitrix/.../show_file.php`) vira URL absoluta
   * em conecta.bitrix24.com.br, para o clique baixar o arquivo como o Abrir
   * de Pedido de Compra e Termo de Aceite. URL já absoluta permanece.
   *
   * @param {string} url Endereço gravado na integração.
   * @returns {string} URL que o navegador deve abrir.
   */
  static resolveBitrixDownloadUrl(url) {
    if (typeof url !== 'string') {
      return url;
    }

    const trimmed = url.trim();
    if (trimmed.startsWith('/')) {
      return `https://conecta.bitrix24.com.br${trimmed}`;
    }

    return trimmed;
  }

  /**
   * Texto do link de anexo. O item do CRM no Formulário do Instrutor
   * aparece como Abrir; arquivos do operador usam o nome de cada arquivo.
   *
   * @param {{ label?: string, name?: string }} file Anexo da lista.
   * @returns {string}
   */
  static attachmentDownloadLabel(file) {
    if (!file || typeof file !== 'object') {
      return 'Arquivo';
    }

    if (file.name === 'BitrixInstructorForm' || file.label === 'Formulário do Instrutor') {
      return 'Abrir';
    }

    const label = typeof file.label === 'string' ? file.label.trim() : '';
    if (label) {
      return label;
    }

    const name = typeof file.name === 'string' ? file.name.trim() : '';
    if (name) {
      return name;
    }

    return 'Arquivo';
  }

  static groupBy(array, key) {
    return array.reduce((acc, item) => {
      if (!acc[item[key]]) acc[item[key]] = [];
      acc[item[key]].push(item);
      return acc;
    }, {});
  }

  /**
   * Lê um cookie pelo nome.
   *
   * @param {string} cname Nome do cookie (ex.: `conecta__refreshToken`).
   * @returns {string} Valor do cookie ou string vazia se não existir.
   */
  static getCookie(cname) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  /**
   * Encerra a sessão: apaga cookies, limpa o `localStorage` do portal
   * (prefixo `conecta__`) e redireciona para `/Login`.
   *
   * @returns {void}
   */
  static delCookies() {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i += 1) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }

    localStorage.removeItem('conecta__token');
    localStorage.removeItem('conecta__refreshToken');
    localStorage.removeItem('conecta__permissions');
    localStorage.removeItem('conecta__userData');
    localStorage.removeItem('conecta__scheduleData');
    localStorage.removeItem('conecta__currentView');
    localStorage.removeItem('conecta__menuPreference');

    window.location.href = '/Login';
  }

  /**
   * Indica se o usuário marcou “permanecer conectado” (cookie de refresh).
   * Sem cookie e com token no `localStorage`, limpa só o storage e retorna `false`.
   * Sem cookie e sem token, chama {@link Utils.delCookies}.
   *
   * @returns {boolean} `true` se existir cookie `conecta__refreshToken`.
   */
  static hasCookies() {
    const refreshTokenCookie = Utils.getCookie('conecta__refreshToken');
    const token = localStorage.getItem('conecta__token');

    if (refreshTokenCookie) {
      return true;
    }
    if (!refreshTokenCookie && token) {
      localStorage.removeItem('conecta__token');
      localStorage.removeItem('conecta__permissions');
      localStorage.removeItem('conecta__userData');
      localStorage.removeItem('conecta__scheduleData');
      localStorage.removeItem('conecta__currentView');
      localStorage.removeItem('conecta__menuPreference');
      return false;
    }

    this.delCookies();
    return false;
  }

  /**
   * Gera um UUID v4 via `crypto.getRandomValues`.
   *
   * @returns {string} UUID no formato `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.
   */
  static generateUUID() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );
  }

  /**
   * Valida CNPJ (com ou sem máscara), via `cpf-cnpj-validator`.
   *
   * @param {string} cnpjValue CNPJ informado.
   * @returns {boolean} `true` se o dígito verificador for válido.
   */
  static validateCNPJ(cnpjValue) {
    let data = cnpjValue;
    data = cnpjValue?.replace(/[^\d]+/g, '');

    const isValid = cnpj?.isValid(data);
    return isValid;
  }

  /**
   * Valida CPF (com ou sem máscara), via `cpf-cnpj-validator`.
   *
   * @param {string} cpfValue CPF informado.
   * @returns {boolean} `true` se o dígito verificador for válido.
   */
  static validateCPF(cpfValue) {
    let data = cpfValue;
    data = cpfValue?.replace(/[^\d]+/g, '');

    const isValid = cpf?.isValid(data);
    return isValid;
  }

  /**
   * Converte HEX (`#RGB` ou `#RRGGBB`) para `rgba(...)`.
   *
   * @param {string} hex Cor hexadecimal com `#`.
   * @param {number} [opacity=1] Alfa de 0 a 1.
   * @returns {string} String CSS `rgba(r,g,b,a)`.
   * @throws {Error} Se o HEX for inválido.
   */
  static hexToRgbA(hex, opacity = 1) {
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      c = hex.substring(1).split('');

      if (c.length === 3) {
        c = [c[0], c[0], c[1], c[1], c[2], c[2]];
      }
      c = `0x${c.join('')}`;

      return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},${opacity})`;
    }
    throw new Error('Código Hex inválido!');
  }

  /**
   * Validador Ant Design para um ou mais e-mails separados por `;`.
   * Campo vazio é aceito (obrigatoriedade fica na `rule` `required`).
   *
   * @param {*} _ Campo do Form (não usado).
   * @param {string} value Texto do input.
   * @returns {Promise<void>} Rejeita com mensagem em português se inválido.
   */
  static validateEmails(_, value) {
    if (!value || value === '') {
      return Promise.resolve();
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = value.split(';').map((email) => email.trim());

    if (emails.some((email) => !emailRegex.test(email))) {
      return Promise.reject(
        new Error('Por favor, insira um ou mais e-mails válidos separados por ponto e vírgula (;).')
      );
    }

    if (/[,/:]/.test(value)) {
      return Promise.reject(
        new Error('Utilize apenas ponto e vírgula (;) para separar os e-mails.')
      );
    }

    return Promise.resolve();
  }

  /**
   * Extrai canais R, G e B de um HEX `#RRGGBB`.
   * Usado por {@link Utils.getColorLightness}.
   *
   * @param {string} colorCode Cor com `#` e 6 dígitos.
   * @returns {number[]} `[r, g, b]` em 0–255.
   */
  static colorCodeToRGB(colorCode) {
    let color = colorCode;
    color = color.substr(1);
    return [color.substr(0, 2), color.substr(2, 2), color.substr(4, 2)].map((it) =>
      parseInt(it, 16)
    );
  }

  /**
   * Lineariza um canal sRGB (0–255) para cálculo de luminância.
   *
   * @param {number} colorChannel Canal 0–255.
   * @returns {number} Canal linearizado.
   */
  static linearizeSRGB(colorChannel) {
    const color = colorChannel;

    if (color <= 0.04045) {
      return color / 12.92;
    }
    return ((colorChannel + 0.055) / 1.055) ** 2.4;
  }

  /**
   * Luminosidade relativa da cor (WCAG), para contraste de texto sobre fundo.
   *
   * @param {string} color HEX `#RRGGBB`.
   * @returns {number} Luminosidade relativa.
   */
  static getColorLightness(color) {
    const luminanceCoefficients = [0.2126, 0.0722, 0.7152];
    const cv = this.colorCodeToRGB(color).map((colorRGB) => this.linearizeSRGB(colorRGB));

    const luminance =
      cv[0] * luminanceCoefficients[0] +
      cv[1] * luminanceCoefficients[1] +
      cv[2] * luminanceCoefficients[2];

    if (luminance <= 216 / 24389) {
      return luminance * (24389 / 27);
    }
    return (luminance ** (1 / 3) * 116 - 16) / 100;
  }

  /**
   * Nome do dia da semana em pt-BR, com inicial maiúscula
   * (fim de semana por extenso; dia útil só o primeiro trecho, ex.: `Segunda`).
   *
   * @param {string|number|Date} date Data parseável por `Date`.
   * @returns {string} Dia da semana.
   */
  static getWeekDayLong(date) {
    const dateFormatted = new Date(date);
    let weekDay = dateFormatted.toLocaleString('pt-br', { weekday: 'long' });

    if (dateFormatted.getDay() === 0 || dateFormatted.getDay() === 6) {
      weekDay = weekDay.charAt(0).toUpperCase() + weekDay.slice(1);
    } else {
      const weekDaySplitted = weekDay.split('-');
      weekDay = weekDaySplitted[0].charAt(0).toUpperCase() + weekDaySplitted[0].slice(1);
    }

    return weekDay;
  }

  /**
   * Mês abreviado em pt-BR (`Jan`, `Fev`, …), sem ponto.
   *
   * @param {string|number|Date} date Data parseável por `Date`.
   * @returns {string} Mês curto.
   */
  static getMonthShort(date) {
    const dateFormatted = new Date(date);
    let weekDay = dateFormatted.toLocaleString('pt-br', { month: 'short' });
    weekDay = weekDay.charAt(0).toUpperCase() + weekDay.slice(1);
    return weekDay.replace('.', '');
  }

  /**
   * Mês por extenso em pt-BR (`Janeiro`, `Fevereiro`, …).
   *
   * @param {string|number|Date} date Data parseável por `Date`.
   * @returns {string} Mês longo.
   */
  static getMonthLong(date) {
    const dateFormatted = new Date(date);
    let weekDay = dateFormatted.toLocaleString('pt-br', { month: 'long' });
    weekDay = weekDay.charAt(0).toUpperCase() + weekDay.slice(1);
    return weekDay.replace('.', '');
  }

  /**
   * Nome do produto com complemento, no padrão `Nome - Complemento`.
   *
   * @param {{ name?: string, complement?: string }} product Produto do treinamento.
   * @returns {string|undefined} Nome exibível.
   */
  static getProductNameComplement(product) {
    if (product?.complement && product?.complement !== '') {
      return `${product?.name}${` - ${product?.complement}` ?? ''}`;
    }

    return product?.name;
  }

  /**
   * Título de exibição do card da agenda: `T{n}/{qtyClass} - D{dia}/{dias}-{grupo}`.
   * Não persiste. Se faltar numeração, totais ou grupo, devolve o `cardTitle` gravado.
   *
   * @param {object} task Treinamento (`classId` ou `cardTitle`, `qtyClass`, `trainingDay`, `totalTrainingDays`, `group.name`).
   * @returns {string} Máscara nova ou o título persistido.
   */
  static formatAgendaCardTitle(task) {
    const persistedTitle = task?.cardTitle ?? '';

    if (!task) {
      return persistedTitle;
    }

    const classNumber = readAgendaClassNumber(task);
    const groupName = readAgendaGroupName(task);

    if (!classNumber || !groupName || !hasAgendaCardTitlePieces(task)) {
      return persistedTitle;
    }

    return `T${classNumber}/${task.qtyClass} - D${task.trainingDay}/${task.totalTrainingDays}-${groupName}`;
  }

  /**
   * Título operacional do card da grade: `T{n}/{qtyClass} - D{dia}/{dias}` sem grupo.
   * Não persiste. Se faltar numeração ou totais, recorta o `cardTitle` gravado (Falha 08).
   *
   * @param {object|null|undefined} task Treinamento (`classId` ou `cardTitle`, `qtyClass`, `trainingDay`, `totalTrainingDays`).
   * @returns {string} Máscara operacional, recorte do título persistido, ou string vazia.
   */
  static formatAgendaGridCardTitle(task) {
    if (!task) {
      return '';
    }

    const classNumber = readAgendaClassNumber(task);

    if (classNumber && hasAgendaCardTitlePieces(task)) {
      return `T${classNumber}/${task.qtyClass} - D${task.trainingDay}/${task.totalTrainingDays}`;
    }

    return readAgendaGridFallbackTitle(task.cardTitle);
  }

  /**
   * Nome exibido na máscara do card da grade da agenda.
   * Lê somente `dealClient` (Nome do Cliente). `dealClientCommercialName` (Nome Fantasia)
   * é ignorado de propósito, para a linha do card não voltar a mostrar a fantasia.
   * Não persiste. Valor vazio, nulo ou só com espaços vira string vazia, e o card omite o parágrafo.
   *
   * @param {object|null|undefined} task Treinamento (`dealClient`).
   * @returns {string} Nome do Cliente, ou string vazia.
   */
  static readAgendaCardClientName(task) {
    return readAgendaCardClientName(task);
  }

  /**
   * View-model do ícone de ônibus da grade: visível só se transporte oficial === 'Incluso'.
   * Cinza quando incluso sem empresa e sem veículo; amarelo com empresa de transporte
   * selecionada ou com veículo/motorista vinculado. Sem React. Não persiste.
   *
   * @param {object|null|undefined} appointmentData Treinamento (`trainingTransportation`, `transportCompany`, `driverList`).
   * @returns {{ visible: boolean, color: ('#fabc2c'|'#b1b1b1'|null), tooltipKind: ('none'|'incluso'|'vehicles'), vehicleIdentifications: string[] }}
   */
  static resolveAgendaTransportIcon(appointmentData) {
    if (!appointmentData || !isOfficialTransportIncluded(appointmentData)) {
      return buildHiddenAgendaTransportIcon();
    }

    return buildIncludedAgendaTransportIcon(
      readLinkedAgendaVehicles(appointmentData),
      hasSelectedTransportCompany(appointmentData)
    );
  }

  /**
   * Combina a data do agendamento com o horário do slot, via Moment.
   * Se a data for inválida (ex.: 31/02), usa o último dia do mês.
   *
   * @param {Date} dateInput Dia do treinamento.
   * @param {Date} slotTime Horário do slot.
   * @returns {Date} Data/hora combinada.
   */
  static createSafeDate(dateInput, slotTime) {
    const date = dateInput;
    const slotDate = slotTime;

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const hours = slotDate.getHours();
    const minutes = slotDate.getMinutes();
    const seconds = slotDate.getSeconds();

    let safeDate = moment([year, month, day, hours, minutes, seconds]);

    if (!safeDate.isValid()) {
      safeDate = moment([year, month]).endOf('month');
    }

    return safeDate.toDate();
  }

  /**
   * Descriptografa payload AES-CBC (Base64) com `REACT_APP_ENCRYPT_KEY` e
   * `REACT_APP_ENCRYPT_IV`. Se não for Base64, devolve o valor original.
   *
   * @param {string} base64Data Texto Base64 ou valor já em claro.
   * @returns {object|string|null} JSON parseado, texto UTF-8 ou `null` se a decriptação falhar.
   */
  static decryptData(base64Data) {
    const base64Pattern = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
    if (!base64Pattern.test(base64Data)) {
      return base64Data;
    }

    const key = CryptoJS.enc.Hex.parse(process.env.REACT_APP_ENCRYPT_KEY);
    const iv = CryptoJS.enc.Hex.parse(process.env.REACT_APP_ENCRYPT_IV);

    const decryptedData = CryptoJS.AES.decrypt(base64Data, key, {
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
      iv,
    });

    const decryptedText = decryptedData.toString(CryptoJS.enc.Utf8);

    if (!decryptedText) {
      return null;
    }

    try {
      return JSON.parse(decryptedText);
    } catch (error) {
      return decryptedText;
    }
  }

  /**
   * Descriptografa campos sensíveis das configurações do portal
   * (senha de e-mail, tokens de API, webhook Bitrix, etc.).
   *
   * @param {object} settings Objeto de settings da API.
   * @returns {object} Cópia com os campos listados já em claro.
   */
  static decryptSettings(settings) {
    const sensitiveFields = [
      'emailPassword',
      'bitrixWebhookUrl',
      'bitrix24QueueMiddlewareAuthToken',
      'holidayApiToken',
      'cnpjApiToken',
      'omieApiKey',
      'omieApiSecret',
      'mapBoxToken',
    ];

    const decryptedSettings = { ...settings };
    sensitiveFields.forEach((field) => {
      if (decryptedSettings[field]) {
        decryptedSettings[field] = this.decryptData(decryptedSettings[field]);
      }
    });

    return decryptedSettings;
  }

  /**
   * Envia o erro para `POST /Log/GenerateFileLog` (chave `REACT_APP_API_EXTERNAL_KEY`).
   * Falha de rede é só `console.warn`, para não gerar loop de log.
   *
   * @param {Error|object} error Erro de Axios, validação ou genérico.
   * @returns {void}
   */
  static sendErrorToAPI(error) {
    if (!process.env.REACT_APP_HOST) {
      // eslint-disable-next-line no-console
      console.warn('REACT_APP_HOST não está configurado. Verifique o arquivo .env.local');
      return;
    }

    const userId = JSON.parse(localStorage.getItem('conecta__userData'))?.id;
    const firstError =
      Array.isArray(error?.errors) && error?.errors?.length > 0 ? error?.errors[0] : null;

    const logModel = {
      UserId: userId,
      Action: error?.config?.method?.toUpperCase() || 'NONE',
      ActionName: 'Frontend',
      Date: new Date().toISOString(),
      ObjectType: firstError?.logref || error?.constructor?.name || 'UnknownError',
      ErrorMessage:
        firstError?.message || error?.response?.data?.message || error?.message || 'Unknown Error',
      ErrorTrace: firstError?.stacktrace || error?.stack || 'No stack trace available',

      ErrorRequestMethod: error?.config?.method?.toUpperCase() || 'NONE',
      ErrorRequestPath: error?.config?.url || 'No URL',
      ErrorRequestQuery: error?.config?.params || '',

      LogType: 'ERROR',
    };

    const endpoint = `${process.env.REACT_APP_HOST}/Log/GenerateFileLog?ExternalKey=${process.env.REACT_APP_API_EXTERNAL_KEY}`;
    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(logModel),
    }).catch((fetchError) => {
      // eslint-disable-next-line no-console
      console.warn('Erro ao enviar log para API:', fetchError);
    });
  }

  /**
   * Ponto único de log do frontend: grava na API e, em produção, no Sentry
   * (ignora 401 e algumas mensagens conhecidas). Em desenvolvimento, só `console.error`.
   *
   * @param {Error|object} error Erro capturado.
   * @returns {void}
   */
  static logError(error) {
    this.sendErrorToAPI(error);

    if (process.env.NODE_ENV === 'production') {
      const isUnauthorizedError = error?.response && error?.response?.status === 401;
      const errorMessage = error?.response?.data?.message;

      const messagesToIgnore = [
        'Email já cadastrado',
        'Usuário não encontrado',
        "(reading 'removeDroppableClasses')",
      ];

      const shouldLogError = !messagesToIgnore.includes(errorMessage);

      if (!isUnauthorizedError && shouldLogError && error) {
        Sentry.captureException(error);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('Erro:', error);
    }
  }

  /**
   * Extrai mensagem amigável de erro padronizado da API (`errors[0].logref`).
   *
   * @param {object} error Payload de erro.
   * @param {string} defaultMessage Fallback se não houver `logref` conhecido.
   * @returns {string} Mensagem para exibir ao usuário.
   */
  static getErrorMessage(error, defaultMessage) {
    try {
      if (error && error?.errors && error?.errors?.length > 0) {
        const firstError = error?.errors[0];
        const validLogrefs = [
          'ValidationError',
          'NotFound',
          'Forbidden',
          'Conflict',
          'BadRequest',
          'Unauthorized',
          'PreconditionFailed',
          'ServiceUnavailable',
        ];

        if (firstError.logref && validLogrefs.includes(firstError.logref)) {
          return firstError.message;
        }
      }
    } catch (e) {
      return defaultMessage;
    }

    return defaultMessage;
  }

  /**
   * Marca/desmarca “selecionar todos” em um `Select` múltiplo do Ant Design.
   * Mutação intencional de `values` para o Form refletir a lista completa.
   *
   * @param {Array<*>} values Valores atuais do Select.
   * @param {Array<{ value: * }>} options Opções, incluindo `selectAll`.
   * @param {string} fieldName Nome do campo no Form.
   * @param {import('antd').FormInstance} form Instância do Form.
   * @returns {void}
   */
  static handleSelectAllChange(values, options, fieldName, form) {
    if (values.includes('selectAll') || values.length === (options?.length ?? 0 - 1)) {
      // eslint-disable-next-line no-param-reassign
      values = options?.map((opt) => opt.value).filter((opt) => opt !== 'selectAll');
      form.setFieldValue(fieldName, values);
      if (values.length === (options?.length ?? 0 - 1)) {
        values.push('selectAll');
      }
    }
  }

  /**
   * Formata número para moeda brasileira (`R$ 1.234,56`), até 4 casas decimais.
   *
   * @param {number|string} value Valor numérico.
   * @returns {string|null} Texto formatado ou `null` se vazio.
   */
  static priceFormatter(value) {
    if (!value) return null;

    const stringValue = `${value}`.replace('.', ',');

    let [integerPart, decimalPart] = stringValue.split(',');

    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    if (decimalPart) {
      decimalPart = decimalPart.slice(0, 4);
    }

    return `R$ ${decimalPart ? `${integerPart},${decimalPart}` : integerPart}`;
  }

  /**
   * Parser de `InputNumber` em reais: remove `R$`, pontos de milhar e
   * troca vírgula por ponto. Retorna float com 4 casas.
   *
   * @param {string} value Texto digitado no campo.
   * @returns {number} Valor numérico.
   */
  static priceParser(value) {
    const numericValue = value.replace(/[R$\s.]/g, '').replace(',', '.');

    const floatValue = parseFloat(numericValue || 0).toFixed(4);

    return parseFloat(floatValue);
  }

  /**
   * Copia um resumo do treinamento para a área de transferência e exibe toast.
   * Usado no card da agenda (`Appointment`) e no drawer (`TaskDrawerExtra`).
   *
   * Campos copiados (um por linha):
   * - Identificação: Id, Id Negócio, Id Turma, Título, Negócio, Cliente, Responsável
   * - Oferta: Produto, Tipo, CT
   * - Inclusões desta alteração:
   *   - Data de Treinamento (`startDate`, `pt-BR`; vazio se ausente)
   *   - Data de Agendamento (`dateScheduling`, `pt-BR`; vazio se ausente)
   *   - Quantidade de Alunos (`qtyStudentsPerClass`)
   *   - Instrutor (`mainInstructor.userName` em maiúsculas; vazio se ausente)
   *   - Instrutor Assistente (`assistantInstructor.userName` em maiúsculas; vazio se ausente)
   *
   * @param {object} task Tarefa/agendamento exibido no card.
   * @param {Date|string} [task.startDate] Data do treinamento.
   * @param {Date|string} [task.dateScheduling] Data em que o agendamento foi feito.
   * @param {number|string} [task.qtyStudentsPerClass] Alunos da turma.
   * @param {{ userName: string }} [task.mainInstructor] Instrutor principal.
   * @param {{ userName: string }} [task.assistantInstructor] Instrutor assistente.
   * @param {Event} [event] Evento de clique; se existir, chama `stopPropagation`.
   * @returns {void}
   */
  static onCopyDetails(task, event) {
    if (event) {
      event.stopPropagation();
    }

    const text =
      `Id: ${task.id}\n` +
      `Id Negócio: ${task.dealId}\n` +
      `Id Turma: ${task.slotClassId ?? ''}\n` +
      `Título: ${task.cardTitle}\n` +
      `Negócio: ${task.dealTitle}\n` +
      `Cliente: ${task.dealClient}\n` +
      `Responsável: ${task.quoteResponsible}\n` +
      `Produto: ${task.text}\n` +
      `Tipo: ${task.trainingType}\n` +
      `CT: ${task.trainingCenter ?? ''}\n` +
      `Data de Treinamento: ${
        task.startDate ? new Date(task.startDate).toLocaleDateString('pt-BR') : ''
      }\n` +
      `Data de Agendamento: ${
        task.dateScheduling ? new Date(task.dateScheduling).toLocaleDateString('pt-BR') : ''
      }\n` +
      `Quantidade de Alunos: ${task.qtyStudentsPerClass ?? ''}\n` +
      `Instrutor: ${task.mainInstructor?.userName?.toUpperCase() ?? ''}\n` +
      `Instrutor Assistente: ${task.assistantInstructor?.userName?.toUpperCase() ?? ''}\n`;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        message.info('Detalhes do Treinamento copiados para a área de transferência!');
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        message.error('Oops. Algo deu errado ao copiar os detalhes do treinamento!');
      });
  }
}

/**
 * Valida um único e-mail (trim). String vazia retorna `false`.
 *
 * @param {string} email Endereço a validar.
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  if (!email || email.trim() === '') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};
